<script setup lang="ts">
/**
 * The standards picker: a grade dropdown and one searchable multi-select over that grade's Common
 * Core catalogue, with the tagged standards as chips BELOW the input (decision B12.5.23) — the
 * trigger has no slot, and a statement is far too long to sit inside an input. The chip carries
 * the code; the statement lives in `title` and in the option's second line.
 *
 * The grade dropdown is the picker's own (RND-98): it defaults to the template's grade and never
 * writes it.
 *
 * Owner-agnostic — an activity's `standards` or one rubric's, per the `owner` prop. Composes the
 * kit's existing `#option` slot; nothing under `src/shared/ui/` was touched (B12.5.22).
 * The four rules are in `standard-select.hook.ts`.
 */
import type { StandardSelectOption, StandardSelectProps } from '../../../model/assessments/standard-select.hook'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppMultiSelectSearchable, AppSelect } from '@shared/ui/selects'
import { BtnIcon } from '@shared/ui/buttons'

import { useStandardSelect } from '../../../model/assessments/standard-select.hook'

defineOptions({
  name: 'StandardSelect',
})

const props = withDefaults(defineProps<StandardSelectProps>(), {
  label: 'Standards',
})

const {
  refOptions,
  refSelectedCodes,
  refChips,
  refIsLoading,
  refIsDisabled,
  refPlaceholder,
  refCatalogueGrade,
  gradeSelectOptions,
  setSelectedCodes,
  removeCode,
} = useStandardSelect({
  owner: () => props.owner,
})
</script>

<template>
  <div class="standard-select">
    <div class="standard-select__row">
      <!-- Labels are the kits' own, so Grade, Standards and Points line up. -->
      <AppSelect
        v-model="refCatalogueGrade"
        class="standard-select__grade"
        label="Grade"
        :options="gradeSelectOptions"
        :disabled="refIsDisabled"
        placeholder="Grade"
      />

      <AppMultiSelectSearchable
        class="standard-select__standards"
        :label="label"
        :model-value="refSelectedCodes"
        :options="refOptions"
        :loading="refIsLoading"
        :disabled="refIsDisabled"
        :placeholder="refPlaceholder"
        no-results-text="No standard matches, in this grade"
        @update:model-value="(codes?: string[]) => setSelectedCodes(codes ?? [])"
      >
        <!-- The kit's own `#option` slot: a two-line row, code then statement, because the
             statement is 150 characters on average and cannot be the row's only label. -->
        <template #option="{ option, selected }">
          <div
            class="standard-select__option"
            :class="{ 'standard-select__option--selected': selected }"
          >
            <AppText name="body2-medium">
              {{ option.text }}
            </AppText>
            <AppText
              name="body2"
              color="slate-500"
              class="standard-select__statement"
            >
              {{ (option as StandardSelectOption).statement }}
            </AppText>
          </div>
        </template>
      </AppMultiSelectSearchable>
    </div>

    <!-- Chips below the input. An off-grade chip renders like any other — no marker (B12.5.25).
         Removable from any grade, re-addable only with its own grade selected. -->
    <ul
      v-if="refChips.length > 0"
      class="standard-select__chips"
    >
      <li
        v-for="chip in refChips"
        :key="chip.code"
        class="standard-select__chip"
        :title="chip.label"
      >
        <AppText name="body2">
          {{ chip.code }}
        </AppText>
        <BtnIcon
          :icon="faXmark"
          size="small"
          view="transparent"
          :title="`Remove ${chip.code}`"
          @click="removeCode(chip.code)"
        />
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss" src="./StandardSelect.scss" />
